import {useState} from "react";
import {useHookClass} from "@dvrt/graphql-widgets";
import {FormattedPageTitle} from "../../pages";
import {DataSource} from "./dataSource";
import {TimeRange, QueueCall} from "./types";
import {
    graph_data_download_in_excel,
    averageCallDuration,
    averageWaitTime,
    averageTalkTime,
    averageSpeedOfAnswer,
} from "./tools";
import Loading from "@/components/breadcrumbs/Loading";
import {useIntl, FormattedMessage, defineMessage} from "react-intl";
import CallChart, {callCountGraph, callDurationGraph} from "./CallChart";
import SelectTimeRange from "./SelectTimeRange";
import FormattedButton from "@/components/FormattedButton";
import {Divider} from "@mui/material";
import {DisconnectReason, SegmentType} from "@/api";

type Props = {
    groupId: number;
};

function connectedByAgent(call: QueueCall): boolean {
    return call.agent.some((c) => c.connected);
}

export default (props: Props) => {
    const dataSource = useHookClass(DataSource, props.groupId, false);
    const intl = useIntl();
    const [timeRange, setTimeRange] = useState<TimeRange>(TimeRange.Today);

    if (!dataSource.isInitialized) return <Loading />;

    // timeFormatDefaultLocale(hungarianLocale as any);

    const countMappings = {
        [intl.formatMessage({id: "call_queue.statistics.graph.answered", defaultMessage: "answered"})]: (
            calls: QueueCall[],
        ) =>
            // calls started by the queue and accepted by the agent
            calls.filter((c) => connectedByAgent(c)).length,

        [intl.formatMessage({id: "call_queue.statistics.graph.abandoned", defaultMessage: "abandoned during wait"})]: (
            calls: QueueCall[],
        ) =>
            // incoming call that has no outgoing pair
            calls.filter(
                (c) =>
                    c.received.segmentType != SegmentType.TransferByBoth &&
                    c.received.disconnectReason == DisconnectReason.CallerDisconnect &&
                    !connectedByAgent(c),
            ).length,

        [intl.formatMessage({id: "call_queue.statistics.graph.rejected", defaultMessage: "rejected"})]: (
            calls: QueueCall[],
        ) =>
            calls.filter(
                (c) =>
                    c.received.segmentType != SegmentType.TransferByBoth &&
                    c.received.disconnectReason == DisconnectReason.Busy,
            ).length,
    };

    const durationMappings = {
        [intl.formatMessage({id: "call_queue.statistics.graph.wait-time", defaultMessage: "avg. wait time"})]: (
            calls: QueueCall[],
        ) => averageWaitTime(calls.filter((c) => c.received.connected && connectedByAgent(c))),

        [intl.formatMessage({id: "call_queue.statistics.graph.abandon-time", defaultMessage: "avg. abandon time"})]: (
            calls: QueueCall[],
        ) =>
            averageCallDuration(
                calls.filter((c) => c.received.connected && !connectedByAgent(c)).map((qc) => qc.received),
            ),

        [intl.formatMessage({id: "call_queue.statistics.graph.talk-time", defaultMessage: "avg. talk time"})]: (
            calls: QueueCall[],
        ) => averageTalkTime(calls.filter((c) => c.received.connected && connectedByAgent(c))),

        [intl.formatMessage({
            id: "call_queue.statistics.graph.speed-of-answer",
            defaultMessage: "avg. speed of answer",
        })]: (calls: QueueCall[]) =>
            averageSpeedOfAnswer(calls.filter((c) => c.received.connected && connectedByAgent(c))),
    };

    const binnedQueueCalls = dataSource.binnedCalls(timeRange);
    return (
        <div style={{marginBottom: 20, width: "fit-content"}}>
            <FormattedPageTitle
                desc={defineMessage({
                    id: "call_queue.statistics.page-title",
                    defaultMessage: "Queue Statistics for { name }",
                })}
                values={{name: dataSource.groupInfo.name}}
            />

            <SelectTimeRange timeRange={timeRange} setTimeRange={setTimeRange} />

            <h3>
                <FormattedMessage id="call_queue.statistics.graph-title.queue-calls" defaultMessage="Number of Calls" />
            </h3>
            <CallChart
                graphType={callCountGraph}
                data={binnedQueueCalls}
                mapping={countMappings}
                timeRange={timeRange}
            />
            <FormattedButton
                message={defineMessage({id: "download_data", defaultMessage: "Download data"})}
                onClick={() =>
                    graph_data_download_in_excel({
                        mappings: countMappings,
                        binnedCalls: binnedQueueCalls,
                        intl,
                        sheetName: defineMessage({
                            id: "call_queue.statistics.graph-title.queue-calls",
                            defaultMessage: "Number of Calls",
                        }),
                        fileName: "queue-call-counts",
                    })
                }
            />
            <Divider style={{marginTop: "2rem", marginBottom: "2rem"}} />
            <h3>
                <FormattedMessage
                    id="call_queue.statistics.graph-title.queue-call-durations"
                    defaultMessage="Duration of Calls"
                />
            </h3>
            <CallChart
                graphType={callDurationGraph}
                data={binnedQueueCalls}
                mapping={durationMappings}
                timeRange={timeRange}
            />
            <FormattedButton
                message={defineMessage({id: "download_data", defaultMessage: "Download data"})}
                onClick={() =>
                    graph_data_download_in_excel({
                        mappings: durationMappings,
                        binnedCalls: binnedQueueCalls,
                        intl,
                        sheetName: defineMessage({
                            id: "call_queue.statistics.graph-title.queue-call-durations",
                            defaultMessage: "Duration of Calls",
                        }),
                        fileName: "queue-call-durations",
                        decimalPlaces: 2,
                    })
                }
            />
        </div>
    );
};
